import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../redux";
import { markNotificationAsRead, deleteNotification } from "../redux/reducers/notificationSlice";
import { Notification } from "../redux/reducers/notificationSlice";
import { View, Text, Icon, Button } from "./base";
import clsx from "clsx";

interface NotificationItemProps {
  notification: Notification;
  onAction?: () => void;
}

export const NotificationItem = ({ notification, onAction }: NotificationItemProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleClick = async () => {
    if (!notification.readAt) {
      await dispatch(markNotificationAsRead(notification.publicId));
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }

    onAction?.();
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(deleteNotification(notification.publicId));
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "account":
        return "user";
      case "system":
        return "info";
      case "agent":
        return "bot";
      case "test":
        return "check-circle";
      case "billing":
        return "credit-card";
      case "function":
        return "code";
      case "community":
        return "users";
      default:
        return "bell";
    }
  };

  const getCategoryColor = (category: string, isRead: boolean) => {
    if (isRead) return "text-gray-400";

    switch (category) {
      case "account":
        return "text-purple-500";
      case "system":
        return "text-blue-500";
      case "agent":
        return "text-green-500";
      case "test":
        return "text-yellow-500";
      case "billing":
        return "text-pink-500";
      case "function":
        return "text-indigo-500";
      case "community":
        return "text-cyan-500";
      default:
        return "text-gray-500";
    }
  };

  const isRead = !!notification.readAt;
  const categoryIcon = getCategoryIcon(notification.category);
  const iconColor = getCategoryColor(notification.category, isRead);

  return (
    <View
      className={clsx(
        "border-b border-gray-200 dark:border-gray-700 transition-colors cursor-pointer",
        !isRead && "bg-blue-50 dark:bg-blue-900/10",
        "hover:bg-gray-50 dark:hover:bg-gray-800",
      )}
      onClick={handleClick}
    >
      <View className="p-4">
        <View className="flex items-start gap-3">
          <View className={clsx("flex-shrink-0 mt-1", iconColor)}>
            <Icon
              name={categoryIcon}
              size={20}
              className={iconColor}
            />
          </View>

          <View className="flex-1 min-w-0">
            <View className="flex items-start justify-between gap-2">
              <Text className={clsx("text-sm font-semibold", isRead ? "text-gray-600 dark:text-gray-400" : "text-gray-900 dark:text-white")}>
                {notification.title}
              </Text>

              <View className="flex items-center gap-2 flex-shrink-0">
                <Text className="text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(notification.createdAt)}</Text>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Icon
                    name="trash"
                    size={14}
                    className="text-red-500"
                  />
                </Button>
              </View>
            </View>

            <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">{notification.message}</Text>

            {!isRead && notification.deletionPolicy === "delete_on_read" && (
              <Text className="text-xs text-gray-500 dark:text-gray-500 mt-1 italic">Auto-deletes when read</Text>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
